import { z } from "zod";

/** Instagram Direct and Messenger, both through the Facebook Page (ADR 0092). */
export const MESSAGING_CHANNELS = ["instagram", "messenger"] as const;
export const messagingChannelSchema = z.enum(MESSAGING_CHANNELS);
export type MessagingChannel = z.infer<typeof messagingChannelSchema>;

export function isMessagingChannel(
  channel: string | null | undefined,
): channel is MessagingChannel {
  return channel === "instagram" || channel === "messenger";
}

export const messagingChannelLabel: Record<MessagingChannel, string> = {
  instagram: "Instagram",
  messenger: "Messenger",
};

/** "ig:<IGSID>" or "fb:<PSID>": the lead's identity on the Page connection. */
export function messagingProviderLeadId(channel: MessagingChannel, contactId: string): string {
  return `${channel === "instagram" ? "ig" : "fb"}:${contactId}`;
}

/** The webhook object of each channel: `page` is Messenger, `instagram` is Direct. */
export function messagingChannelOfObject(object: string): MessagingChannel | null {
  if (object === "page") return "messenger";
  if (object === "instagram") return "instagram";
  return null;
}

const HOUR_MS = 60 * 60 * 1_000;
/** Any message, from the contact's last one. */
export const MESSAGING_STANDARD_WINDOW_MS = 24 * HOUR_MS;
/** Only a person's reply with the HUMAN_AGENT tag, once Meta has approved it. */
export const MESSAGING_HUMAN_AGENT_WINDOW_MS = 7 * 24 * HOUR_MS;

export type MessagingSendMode = "response" | "human_agent" | "closed";

/**
 * How Polaris can write now: within 24 hours of the contact's last message any
 * message (`RESPONSE`); up to 7 days only a reply with HUMAN_AGENT, if the Page has
 * it; after that nothing until the contact writes again.
 */
export function messagingSendMode(input: {
  lastInboundAt: Date | null;
  humanAgent: boolean;
  now: Date;
}): MessagingSendMode {
  if (!input.lastInboundAt) return "closed";
  const elapsed = input.now.getTime() - input.lastInboundAt.getTime();
  if (elapsed < MESSAGING_STANDARD_WINDOW_MS) return "response";
  if (input.humanAgent && elapsed < MESSAGING_HUMAN_AGENT_WINDOW_MS) return "human_agent";
  return "closed";
}

/** Until when Polaris can write, for the chip in the chat; null = not now. */
export function messagingWindowExpiresAt(input: {
  lastInboundAt: Date | null;
  humanAgent: boolean;
}): Date | null {
  if (!input.lastInboundAt) return null;
  return new Date(
    input.lastInboundAt.getTime() +
      (input.humanAgent ? MESSAGING_HUMAN_AGENT_WINDOW_MS : MESSAGING_STANDARD_WINDOW_MS),
  );
}

const MESSENGER_MAX_CHARACTERS = 2_000;
const INSTAGRAM_MAX_BYTES = 1_000;

/** Meta's limit on one text, as a message for the operator; null = it fits. */
export function messagingTextError(channel: MessagingChannel, text: string): string | null {
  if (channel === "messenger" && [...text].length > MESSENGER_MAX_CHARACTERS) {
    return `Su Messenger un messaggio può avere al massimo ${MESSENGER_MAX_CHARACTERS} caratteri.`;
  }
  if (channel === "instagram" && new TextEncoder().encode(text).length > INSTAGRAM_MAX_BYTES) {
    return "Su Instagram il messaggio è troppo lungo: dividilo in due.";
  }
  return null;
}

const attachmentSchema = z
  .object({
    type: z.string(),
    payload: z
      .object({
        url: z.string().optional(),
        title: z.string().optional(),
        sticker_id: z.number().optional(),
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();

/** One event of `entry[].messaging[]`; unknown fields are kept, unknown events skipped. */
export const messagingEventSchema = z
  .object({
    sender: z.object({ id: z.string().min(1) }).passthrough(),
    recipient: z.object({ id: z.string().min(1) }).passthrough(),
    timestamp: z.number(),
    message: z
      .object({
        mid: z.string().min(1),
        text: z.string().optional(),
        is_echo: z.boolean().optional(),
        app_id: z.union([z.number(), z.string()]).optional(),
        attachments: z.array(attachmentSchema).optional(),
        reply_to: z
          .object({
            mid: z.string().optional(),
            story: z.object({ url: z.string().optional(), id: z.string().optional() }).optional(),
          })
          .passthrough()
          .optional(),
        is_deleted: z.boolean().optional(),
        is_unsupported: z.boolean().optional(),
      })
      .passthrough()
      .optional(),
    delivery: z
      .object({ mids: z.array(z.string()).optional(), watermark: z.number() })
      .passthrough()
      .optional(),
    read: z
      .object({ watermark: z.number().optional(), mid: z.string().optional() })
      .passthrough()
      .optional(),
    reaction: z
      .object({
        mid: z.string().min(1),
        action: z.enum(["react", "unreact"]),
        reaction: z.string().optional(),
        emoji: z.string().optional(),
      })
      .passthrough()
      .optional(),
    postback: z
      .object({ mid: z.string().optional(), title: z.string().optional() })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type MessagingEvent = z.infer<typeof messagingEventSchema>;

export type MessagingEventType =
  | "message"
  | "message_echo"
  | "delivery"
  | "read"
  | "reaction"
  | "postback";

/** What an event is; null for the ones Polaris does not use (typing, referral…). */
export function messagingEventType(event: MessagingEvent): MessagingEventType | null {
  if (event.message) return event.message.is_echo ? "message_echo" : "message";
  if (event.reaction) return "reaction";
  if (event.delivery) return "delivery";
  if (event.read) return "read";
  if (event.postback) return "postback";
  return null;
}

/**
 * The contact of the chat: the sender of what they write, the recipient of what
 * the Page sends (echoes).
 */
export function messagingContactId(event: MessagingEvent): string {
  return event.message?.is_echo ? event.recipient.id : event.sender.id;
}

/**
 * The redirect the Facebook SDK gives its login popup: its own page on
 * staticxx.facebook.com, new at each login. Meta wants it back to exchange the code.
 */
export function isMetaSdkRedirectUri(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.hostname === "staticxx.facebook.com" &&
      url.pathname.startsWith("/x/connect/xd_arbiter")
    );
  } catch {
    return false;
  }
}

/** The code of the Facebook login popup, exchanged server side (ADR 0092). */
export const messagingLoginInputSchema = z
  .object({
    code: z.string().min(8).max(4_096),
    redirectUri: z.string().max(2_048).refine(isMetaSdkRedirectUri).optional(),
  })
  .strict();

/** The Page chosen among those the login gave. */
export const messagingConnectInputSchema = z
  .object({ selectionId: z.string().min(16).max(128), pageId: z.string().min(1).max(64) })
  .strict();

/** HUMAN_AGENT, switched on by the owner once Meta has approved it. */
export const messagingSettingsInputSchema = z
  .object({ humanAgent: z.boolean(), version: z.number().int().positive() })
  .strict();

export type MessagingSetupDto = {
  available: boolean;
  appId: string | null;
  configId: string | null;
};
export type MessagingPagesDto = {
  selectionId: string;
  pages: Array<{ id: string; name: string; instagramUsername: string | null }>;
};
